import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { countBy, isEmpty, keyBy, mapValues, noop } from 'lodash-es';
import { ChipButton, ConfirmationModal, DropdownMenu, FilterPickerGroup, FunctionalLoader, Page, PageContentHeader, PageErrorState, TableList, Typography, useDevice, useStateInRef } from 'seagull';
import { Archive, ChevronDown, ChevronUp, Copy } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import ServiceBuilder from '~/components/ServiceBuilder/ServiceBuilder';
import TableFilters from '~/components/TableFilters/TableFilters';
import { useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useAccountingItems } from '~/hooks/reactQuery/queries/useAccountingItems';
import { useServiceTemplates } from '~/hooks/reactQuery/queries/useServiceTemplates';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { serviceModel, serviceTemplateModel } from '~/models';
import { ServiceSource } from '~/models/service/model';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import styles from './CommonServicesPage.module.scss';
import { defaultSort, getDuplicatedServices, getServiceNameValidation, saveServiceTemplate, serviceNameTextsPath, sortFunctions, sortIds } from './CommonServicesPage.utils';
import { RowItem } from './RowItem';
export function CommonServicesPage(param) {
    let { headerActions, templateType, renderEmptyState, title, subtitle, columnDefs, headerItems, bundlesMode: isBundlesMode, mobileSortOptions } = param;
    const { isMobileDevice } = useDevice();
    const serviceTemplateApi = useServiceTemplateApi();
    const overlayContext = useOverlayContext();
    const { t: texts } = useLocaleKeys('pages.servicesPage');
    const { t: bundlesTexts } = useLocaleKeys('pages.bundlesPage');
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { t: serviceNameTexts } = useLocaleKeys(serviceNameTextsPath);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { data, isFetching, error, refresh: fetchMethod } = useServiceTemplates(templateType, {
        refreshOnHookMount: true
    });
    const { items: qboItems } = useAccountingItems(Accounting.QBO, {
        enabled: showQboIntegration,
        refreshOnHookMount: true
    });
    const { items: xeroItems } = useAccountingItems(Accounting.Xero, {
        enabled: showXeroIntegration,
        refreshOnHookMount: true
    });
    const { logBI, fetchIntegrationsSummary } = useStoreActions();
    const sortIndices = useMemo(()=>sortIds(headerItems), [
        headerItems
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds: sortIndices
    });
    const { urlState: { search }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        }
    });
    const [selected, setSelected] = useState({});
    const getSelected = useStateInRef(selected);
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const allServices = data === null || data === void 0 ? void 0 : data.active;
    useSetDocumentTitle(title);
    useEffect(()=>{
        // this unselects any service that is not found in the newly fetched list of services
        if (!data || isEmpty(getSelected())) return;
        const ids = data.active.map((service)=>service.id);
        const newSelected = mapValues(getSelected(), (origValue, serviceId)=>ids.includes(serviceId) ? origValue : false);
        setSelected(newSelected);
    }, [
        data
    ]);
    useEffect(()=>{
        if (showQboIntegration || showXeroIntegration) {
            fetchIntegrationsSummary();
        }
    }, [
        showQboIntegration,
        showXeroIntegration,
        fetchIntegrationsSummary
    ]);
    const qboItemsById = useMemo(()=>keyBy(qboItems === null || qboItems === void 0 ? void 0 : qboItems.items, 'id'), [
        qboItems === null || qboItems === void 0 ? void 0 : qboItems.items
    ]);
    const xeroItemsById = useMemo(()=>keyBy(xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items, 'id'), [
        xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items
    ]);
    const services = useMemo(()=>{
        if (!data) return null;
        const normalizedSearch = search ? search.trim().toLowerCase() : search;
        const filtered = data.active.filter((service)=>{
            return !normalizedSearch || service.name.toLowerCase().includes(normalizedSearch);
        });
        if (sort) {
            filtered.sort(sortFunctions[sortIndices[sort.colIndex]](sort.sort === 'asc', qboItemsById));
        }
        return filtered;
    }, [
        data,
        qboItemsById,
        search,
        sort,
        sortIndices
    ]);
    const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
    async function openBuilder(serviceToEdit) {
        if (serviceToEdit) {
            logBI(11102, {
                serviceId: serviceToEdit.id
            });
        }
        const serviceNameBlacklist = data === null || data === void 0 ? void 0 : data.active.filter((s)=>s.id !== (serviceToEdit === null || serviceToEdit === void 0 ? void 0 : serviceToEdit.id)).map((s)=>s.name);
        const serviceTemplateSaverListener = {
            saveStarted: ()=>overlayContext.startLoader(title),
            saveFailed: ()=>overlayContext.showErrorToast(texts.failedToSaveService(), 'save-service-fail-toast'),
            saveFinished: ()=>overlayContext.stopLoader(title),
            linkFailed: ()=>overlayContext.showErrorToast(texts.failedToLinkService(), 'link-service-fail-toast')
        };
        const serviceTemplateRepo = {
            create: serviceTemplateApi.create,
            update: serviceTemplateApi.update
        };
        const storePopulator = {
            populateServiceTemplates: async ()=>{
                await fetchMethod();
            }
        };
        const onSave = (output, serviceContentObj)=>{
            return handlePromise(saveServiceTemplate({
                listener: serviceTemplateSaverListener,
                repo: serviceTemplateRepo,
                modifiedService: serviceTemplateModel.fromService(output),
                originalService: serviceToEdit,
                serviceContentObj,
                storePopulator
            }));
        };
        setRegisterSearchBoxHotkey(false);
        try {
            const service = serviceToEdit && serviceModel.fromServiceTemplate(serviceToEdit, ServiceSource.Draft);
            await overlayContext.showModal({
                biName: 'service_builder_modal',
                content: (p)=>/*#__PURE__*/ _jsx(ServiceBuilder, _object_spread_props(_object_spread({}, p), {
                        serviceOrigin: "template",
                        service: service,
                        serviceNameBlacklist: serviceNameBlacklist,
                        onSave: onSave,
                        serviceUsedIn: serviceToEdit === null || serviceToEdit === void 0 ? void 0 : serviceToEdit.usedIn,
                        isCreatingBundle: isBundlesMode
                    }))
            });
        } finally{
            setRegisterSearchBoxHotkey(true);
        }
    }
    function onToggleAllSelection(selectAll) {
        if (services == null) return;
        setSelected(services.reduce((acc, param)=>{
            let { id } = param;
            return Object.assign(acc, {
                [id]: selectAll
            });
        }, {}));
    }
    async function editService(originalService, edit) {
        try {
            logBI(11101, {
                serviceId: originalService.id
            });
            await serviceTemplateApi.update(_object_spread({}, originalService, edit));
            await fetchMethod();
        } catch (err) {
            overlayContext.showErrorToast(texts.failedToSaveChanges(), 'service-library-update-error-toast');
        }
    }
    function getSelectedServices() {
        const services = keyBy(data === null || data === void 0 ? void 0 : data.active, 'id');
        return Object.entries(selected).filter((param)=>{
            let [, isSelected] = param;
            return isSelected;
        }).flatMap((param)=>{
            let [serviceId] = param;
            return serviceId in services ? services[serviceId] : [];
        });
    }
    function bulkActionDecorator(action) {
        return async ()=>{
            const promises = getSelectedServices().map((service)=>action(service, false));
            await Promise.all(promises);
            await fetchMethod();
        };
    }
    async function duplicateServices(services) {
        try {
            overlayContext.startLoader(title);
            services.forEach((s)=>logBI(11104, {
                    serviceId: s.id
                }));
            const duplicatedServices = getDuplicatedServices(services, allServices);
            await Promise.all(duplicatedServices.map(serviceTemplateApi.create));
            await fetchMethod();
        } catch (err) {
            overlayContext.showErrorToast(texts.failedToDuplicateService());
        } finally{
            overlayContext.stopLoader(title);
        }
    }
    async function doDelete(service) {
        let refetch = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : true;
        try {
            logBI(11103, {
                serviceId: service.id
            });
            overlayContext.startLoader(title);
            await serviceTemplateApi.delete(service.id);
            if (refetch) {
                await fetchMethod();
            }
        } catch (err) {
            overlayContext.showErrorToast(texts.failedToDeleteService());
        } finally{
            overlayContext.stopLoader(title);
        }
    }
    async function deleteService(service) {
        const confirmed = await overlayContext.showModal({
            biName: 'common_services_page_delete_confirm',
            closeOnClickOutside: true,
            shouldBlurBackground: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'common_services_confirm'
                        },
                        secondaryButton: {
                            name: 'common_services_cancel'
                        }
                    },
                    title: texts.deleteTitle(),
                    content: texts.deleteContent(),
                    skin: "danger",
                    primaryButtonLabel: texts.delete(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    secondaryButtonLabel: commonButtons.cancel(),
                    onSecondaryButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (confirmed) {
            return doDelete(service);
        }
    }
    const renderPageContent = ()=>{
        var _ref;
        return /*#__PURE__*/ _jsxs(TableList.Provider, {
            hasActionColumn: true,
            columnDefs: columnDefs,
            sort: sort,
            onSortChange: changeSort,
            enableRowSelection: true,
            selectedRows: selected,
            onSelectionChange: setSelected,
            numRows: (services === null || services === void 0 ? void 0 : services.length) || 0,
            numSelectedRows: numSelected,
            onToggleAllSelection: onToggleAllSelection,
            children: [
                ((_ref = data === null || data === void 0 ? void 0 : data.active.length) !== null && _ref !== void 0 ? _ref : 0) > 0 && /*#__PURE__*/ _jsxs(PageContentHeader, {
                    children: [
                        /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                            children: [
                                /*#__PURE__*/ _jsx(TableFilters, {
                                    testIdPrefix: "service-library",
                                    filterLabels: {},
                                    filterConfig: {},
                                    selectedFilters: {},
                                    setSelectedFilters: noop,
                                    onRefresh: (_filters, newSort)=>{
                                        if (newSort) changeSort(newSort);
                                    },
                                    search: {
                                        bi: {
                                            name: 'services_search'
                                        },
                                        value: search,
                                        testId: 'service-library-search',
                                        registerHotkey: registerSearchBoxHotkey,
                                        placeholder: commonTexts.searchPlaceholder(),
                                        onChange: (event)=>{
                                            const search = event.target.value;
                                            changeUrlState({
                                                search
                                            });
                                        },
                                        onClear: ()=>changeUrlState({
                                                search: ''
                                            })
                                    },
                                    mobile: {
                                        sortOptions: mobileSortOptions
                                    }
                                }),
                                !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            className: styles.selectedItems,
                                            testId: "service-library-bulk-counter",
                                            children: numSelected > 0 ? isBundlesMode ? bundlesTexts.bulkSelectedCount({
                                                count: numSelected
                                            }) : texts.bulkSelectedCount({
                                                count: numSelected
                                            }) : ''
                                        }),
                                        /*#__PURE__*/ _jsxs(DropdownMenu, {
                                            onOpenChange: setBulkActionsOpen,
                                            target: /*#__PURE__*/ _jsx(ChipButton, {
                                                bi: {
                                                    name: 'services_library_bulk_actions'
                                                },
                                                testId: "service-library-bulk-actions",
                                                suffixIcon: bulkActionsOpen ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {}),
                                                highlight: bulkActionsOpen,
                                                disabled: numSelected === 0,
                                                children: texts.bulkActions()
                                            }),
                                            children: [
                                                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                                    bi: {
                                                        name: 'service_library_bulk_duplicate'
                                                    },
                                                    iconStart: /*#__PURE__*/ _jsx(Copy, {}),
                                                    testId: "service-library-bulk-duplicate",
                                                    onClick: ()=>duplicateServices(getSelectedServices()),
                                                    children: texts.duplicate()
                                                }),
                                                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                                    bi: {
                                                        name: 'service_library_bulk_delete'
                                                    },
                                                    skin: "error",
                                                    iconStart: /*#__PURE__*/ _jsx(Archive, {}),
                                                    testId: "service-library-bulk-delete",
                                                    onClick: bulkActionDecorator(doDelete),
                                                    children: texts.delete()
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(TableList.Header, {
                            items: headerItems,
                            testId: "service-library-table-header"
                        })
                    ]
                }),
                services === null || services === void 0 ? void 0 : services.map((service)=>/*#__PURE__*/ _jsx(RowItem, {
                        service: service,
                        qboItemsById: qboItemsById,
                        xeroItemsById: xeroItemsById,
                        serviceNameValidation: getServiceNameValidation({
                            skipServiceId: service.id,
                            allServices,
                            texts: serviceNameTexts
                        }),
                        onInlineEdit: (edit)=>editService(service, edit),
                        onEdit: ()=>openBuilder(service),
                        onDuplicate: ()=>duplicateServices([
                                service
                            ]),
                        onDelete: ()=>deleteService(service),
                        onInputFocus: ()=>setRegisterSearchBoxHotkey(false),
                        onInputBlur: ()=>setRegisterSearchBoxHotkey(true),
                        columns: headerItems.map((param)=>{
                            let { id } = param;
                            return id;
                        }),
                        isHeightFluid: isBundlesMode,
                        isMobileDevice: isMobileDevice,
                        bundlesMode: !!isBundlesMode
                    }, service.id)),
                (data === null || data === void 0 ? void 0 : data.active.length) === 0 && renderEmptyState({
                    openBuilder
                })
            ]
        });
    };
    if (error) return /*#__PURE__*/ _jsx(PageErrorState, {
        title: title,
        onRefresh: ()=>fetchMethod()
    });
    return /*#__PURE__*/ _jsx(Page, {
        testId: "service-library",
        header: title + (data && data.active.length > 0 ? " (".concat(data.active.length, ")") : ''),
        subheader: subtitle,
        loading: isFetching,
        variant: (data === null || data === void 0 ? void 0 : data.active.length) === 0 ? 'containedHeight' : undefined,
        actions: headerActions({
            openBuilder
        }),
        children: data ? renderPageContent() : /*#__PURE__*/ _jsx(FunctionalLoader, {
            show: true,
            name: title
        })
    });
}
